import { useState } from "react";
import { TelegramOwnershipForm } from "../TelegramOwnershipForm";
import { actionIssueFrom, actionIssueFromThrown, type ActionIssue } from "../action-error";
import {
  ActionError,
  DataError,
  formatDate,
  isRecord,
  refreshClientFact,
  resource,
  type DeliverySectionContentProps
} from "./shared";
import { readOwnerRoleFix } from "../../shared/owner-role";
import "./handover.css";

// F105 (Pavel 2026-08-10): the final handover is a ladder of DEPENDENT steps,
// the beta pattern. Instance ownership: the client's Telegram ID, the team
// sets the owner role by it. The Owner Dashboard: locked until the ownership
// step, because /connectWeb and /myLicense are OWNER commands and cannot
// answer before the role lands. The business-proof table left this surface
// with the S20 re-anchoring: the handover closes on those two facts. Every
// label in the dashboard walkthrough is the REAL dashboard label, read from
// the dashboard's own cs/en locale files and the live sign-up form.
//
// F108 (Pavel 2026-08-10) made the Buddy's folder step 1 of this ladder and
// F111 (Pavel 2026-08-10) then stripped it to a title, a button and one
// sentence. F133 (Pavel 2026-08-11) removes it from this surface ENTIRELY,
// with a new fact behind the decision: the folder link is plainly visible in
// the client's own Buddy dashboard, so restating it here was a step about
// nothing. Verbatim: "ten krok jedna v predani slozka tveho buddyho ten dejme
// uplne pryc... vizualne tady vubec nemusi byt ten krok 'slozka tveho buddyho
// je tvoje'. Ted nemusis nic delat. To tam vizualne vubec byt nemusi a vubec
// by to nemelo byt v tom predani."
//
// 🔴 The BACKGROUND is untouched. "A jenom potrebujeme tu slozku vlastne na
// pozadi nazdilet na tu e-mailovou adresu toho uzivatele, pres kterou to
// upravoval." The Mac Mini sharing and verification lanes (F107/F109/F110
// triggers, `handoverAutomation`, the machine API, the partner queue row) all
// keep running exactly as before; only this render lost the card. So nothing
// in worker/src moved, and `handoverSettled` stays the one predicate that
// decides whether the folder is really the client's.
//
// The ladder is TWO steps now: the Telegram ID (1) and the Owner Dashboard
// (2). The locale keys still read stepN from the F105 numbering (step2Title is
// the dashboard step, now numbered 2 again). Renaming them would rewrite a hot
// shared file for nothing: the numbers the client reads come from this ladder.
//
// F133b: the dashboard walkthrough describes the REAL flow, read off the two
// screenshots Pavel sent (`runs/f133-dashboard-*.png`). After sign-up the
// dashboard takes over BY ITSELF - there is no "Uz mam experta" choice in the
// live product - and it shows two whole screens: "Nejprve synchronizujte sveho
// BuddyPro" (/update, then Zkontrolovat stav) and "Pripojte sveho existujiciho
// experta" (Licencni klic + Propojovaci kod). 🔴 That REVERSES the F105 note
// calling `/update` a nonsense as a standalone step: the live product makes it
// an entire screen, so the walkthrough names it as one.

const DASHBOARD_ORIGIN = "https://dashboard.buddypro.ai";
const DOCS_SECTION = "setup/activate-license#alternative-connecting-an-existing-expert-i-already-have-an-expert";

export function HandoverSection(props: DeliverySectionContentProps) {
  // Hooks BEFORE the locked early-return: a step that unlocks while mounted
  // must not change the hook count between renders (the fix-q1flow #310 law).
  const [confirming, setConfirming] = useState(false);
  const [confirmIssue, setConfirmIssue] = useState<ActionIssue | null>(null);
  const section = props.sections.find((item) => item.key === "handover");
  const handoverState = props.salesSteps.find((item) => item.key === "handover")?.state ?? section?.state ?? "locked";
  // F101: the rail step IS the lock. The extra stripe_receiving condition
  // that used to hide the handover content behind a payment confirmation is
  // gone - approved materials open the step, and the step opens the screen.
  if (handoverState === "locked") {
    return (
      <section className="m5b-view" data-handover-locked="true" aria-labelledby="handover-title">
        <header className="m5b-heading">
          <p className="eyebrow blue-eyebrow">{props.t("handover.eyebrow")}</p>
          <h1 id="handover-title">{props.t("rail.steps.handover")}</h1>
          <p className="lead">{props.t("handover.lockedCopy")}</p>
        </header>
        <article className="handover-locked-preview" data-locked-preview="handover">
          <div>
            <p className="card-kicker">{props.t("handover.lockedEyebrow")}</p>
            <h2>{props.t("handover.lockedTitle")}</h2>
          </div>
          <ol>
            {(["materials", "ownership", "dashboard"] as const).map((key) => (
              <li key={key}>
                <span className={`status-pill ${key === "materials" ? "pill-client" : "pill-locked"}`}>
                  {props.t(key === "materials" ? "handover.lockedWaiting" : "handover.lockedLater")}
                </span>
                <strong>{props.t(`handover.lockedSteps.${key}`)}</strong>
              </li>
            ))}
          </ol>
          <p className="handover-locked-next">{props.t("handover.lockedNext")}</p>
        </article>
      </section>
    );
  }
  const license = resource(props.resources, "license")?.value;
  const licenseKeyValue = resource(props.resources, "license_key")?.value;
  // The server withholds the key itself until the ownership step is done
  // (resources.ts), so a present field here is a key the client may really use.
  const licenseKey = typeof licenseKeyValue?.license_key === "string" && licenseKeyValue.license_key
    ? String(licenseKeyValue.license_key)
    : null;
  const telegram = resource(props.resources, "handover_telegram")?.value;
  const trial = resource(props.resources, "trial_link")?.value;
  const confirmations = resource(props.resources, "step_confirmations")?.value;
  const dashboardRecord = isRecord(confirmations?.dashboard) ? confirmations.dashboard : null;
  const dashboardConfirmed = dashboardRecord?.confirmed === true;
  const selling = handoverState === "done";
  const botUsername = typeof trial?.bot_username === "string" && trial.bot_username ? String(trial.bot_username) : null;
  const ownershipReady = typeof telegram?.telegram_id === "string" && Boolean(telegram.telegram_id);
  // F133: the folder card left this surface, so nothing here reads
  // `knowhow_handover` / `instance_folder` / `knowhow_google_account` any
  // more. The FACTS are unchanged and still drive the Mac Mini's sharing and
  // verification lanes (F107/F109/F110) plus the partner queue; they simply
  // stopped being drawn for the client, who already sees the folder link in
  // their own Buddy dashboard.
  // F110: the signal to the Mac Mini goes out the moment this step opens, so
  // the honest wait is short. Past the server's grace window the copy stops
  // promising "any moment now" and says we know and will come back - never an
  // endless "připravujeme" with no explanation.
  const licenseDelayed = props.handoverAutomation?.licenseKey === "delayed";
  // F118 (Pavel 2026-08-11): storing the Telegram ID is not the same fact as
  // the owner role being SET. Step 2 used to close by promising the client
  // could "rovnou pokračovat krokem 3", and step 3 runs on /connectWeb and
  // /myLicense - two OWNER-role commands that answer nothing until a human has
  // set the role inside the instance. So the role is its own recorded fact and
  // every sentence about it reads that fact instead of assuming it.
  const ownerRoleSet = typeof telegram?.owner_role_set_at === "string"
    && String(telegram.owner_role_set_at).length > 0;
  // F128: the role sets itself now, so a CLIENT-fault failure has to land
  // somewhere the client can act on. `readOwnerRoleFix` accepts only the two
  // reasons the client can actually fix (the shared catalog decides, never a
  // list typed out here - that is how a server-side reason would silently
  // render as "pracujeme" forever, the F112 bug). The note lives on the same
  // resource as the ID, so a corrected submit clears it by rebuilding the value.
  const ownerRoleFix = ownerRoleSet ? null : readOwnerRoleFix(telegram);
  const ownerRoleFixOpen = Boolean(ownerRoleFix) && !selling && props.membershipRole === "owner" && Boolean(props.csrfToken);
  const ownershipDone = selling || ownershipReady;
  // The step-2 MARK only. Step 3 keeps reading `ownershipDone`, so a client
  // fixing their ID does not lose the account registration and the licence key.
  const ownershipStepDone = ownershipDone && !ownerRoleFixOpen;
  // A finished handover proves the role was set: the client could not have
  // confirmed a dashboard that never connected.
  const ownerRoleReady = selling || dashboardConfirmed || ownerRoleSet;
  const dashboardDone = selling || dashboardConfirmed;
  const dashboardBase = `${DASHBOARD_ORIGIN}/${props.locale === "cs" ? "cs" : "en"}`;
  // The docs site serves English at the root and Czech under /cs (the
  // Docusaurus i18n default), so both links share one base.
  const docsBase = props.locale === "cs" ? "https://docs.buddypro.ai/cs/" : "https://docs.buddypro.ai/";
  const docsUrl = `${docsBase}${DOCS_SECTION}`;
  const accesses = Array.isArray(license?.accesses) ? license.accesses.filter(isRecord) : [];

  async function confirmDashboard() {
    if (!props.csrfToken || confirming) return;
    setConfirming(true);
    setConfirmIssue(null);
    try {
      const response = await fetch("/api/client-steps/dashboard/confirm", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        setConfirmIssue(await actionIssueFrom(response));
        return;
      }
      // F118: this step's mark comes from `step_confirmations` - a RESOURCE.
      // Reloading the home payload alone leaves the ladder exactly as it was.
      await refreshClientFact(props);
    } catch {
      setConfirmIssue(actionIssueFromThrown());
    } finally {
      setConfirming(false);
    }
  }

  return (
    <section className="m5b-view" aria-labelledby="handover-title">
      {selling && <div className="selling-celebration" aria-hidden="true"><span /><span /><span /></div>}
      <header className={`m5b-heading ${selling ? "selling-heading" : ""}`}>
        {selling && <span className="selling-badge" aria-hidden="true">🎉</span>}
        <p className="eyebrow blue-eyebrow">{props.t("handover.eyebrow")}</p>
        <h1 id="handover-title">{props.t("rail.steps.handover")}</h1>
        <p className="lead">{selling ? props.t("handover.sellingCopy") : props.t("handover.copy")}</p>
        {selling && section && <time dateTime={section.changedAt}>{props.t("handover.sellingDate").replace("{date}", formatDate(section.changedAt, props.locale))}</time>}
      </header>

      {(props.dataErrors?.has("resources") || props.dataErrors?.has("milestones")) && <DataError t={props.t} />}

      <ol className="flow-steps handover-flow">
        {/* Step 1 (F133 numbering): instance ownership. Done = the Telegram ID
            is stored (the beta pattern: the mark mirrors the stored fact, and a
            finished step stays on the page, marked, never removed - K06).
            🔴 F128: except while the automation has handed the step BACK. A
            green check over an open form is the step telling the client two
            opposite things at once, so the MARK follows the client's turn -
            while `ownershipDone` keeps gating step 2, which stays open for the
            registration and the licence key the client really can do (F118). */}
        <li
          className={`flow-step${ownershipStepDone ? " flow-step-done" : ""}`}
          data-handover-step="1"
          data-handover-step-state={ownershipStepDone ? "done" : "open"}
        >
          <header className="flow-step-head">
            <span className={`flow-step-num step-number-token${ownershipStepDone ? " flow-step-num-done is-done" : ""}`} aria-hidden="true">{ownershipStepDone ? "✓" : "1"}</span>
            <h2>{props.t("handover.whatOwnershipTitle")}</h2>
          </header>
          <p className="flow-step-copy">{props.t("handover.whatOwnershipCopy")}</p>
          {ownerRoleFixOpen && ownerRoleFix && props.csrfToken ? (
            /* The named sentence plus the form, the F44/F112 client_fix shape:
               a client who is told what is wrong and given nothing to press has
               been told nothing. */
            <>
              <p className="flow-step-copy handover-role-fix" data-owner-role-fix={ownerRoleFix.reason}>
                {props.t(`handover.ownerRoleFix.${ownerRoleFix.reason}`)}
              </p>
              <TelegramOwnershipForm
                csrfToken={props.csrfToken}
                botUsername={botUsername}
                storedUsername={typeof telegram?.username === "string" && telegram.username ? String(telegram.username) : null}
                mode="ownership"
                t={props.t}
                onSaved={() => refreshClientFact(props)}
              />
            </>
          ) : ownershipReady ? (
            <>
              <p className="handover-telegram-note">
                {props.t("handover.telegramStored")}{" "}
                {typeof telegram?.username === "string" && telegram.username ? <strong>@{String(telegram.username)}</strong> : null}
                {typeof telegram?.telegram_id === "string" && telegram.telegram_id ? <> · ID <strong>{String(telegram.telegram_id)}</strong></> : null}
              </p>
              {/* The one sentence Pavel caught lying. It now names the real
                  state: the role is set, or the team is still setting it and
                  will say when it is done. */}
              {!selling && (
                <p className="flow-step-copy handover-role-note" data-owner-role={ownerRoleReady ? "set" : "waiting"}>
                  {props.t(ownerRoleReady ? "handover.step1DoneReady" : "handover.step1DoneWaiting")}
                </p>
              )}
            </>
          ) : selling ? null : props.membershipRole === "owner" && props.csrfToken ? (
            <TelegramOwnershipForm
              csrfToken={props.csrfToken}
              botUsername={botUsername}
              storedUsername={typeof telegram?.username === "string" && telegram.username ? String(telegram.username) : null}
              mode="ownership"
              t={props.t}
              onSaved={() => refreshClientFact(props)}
            />
          ) : (
            <>
              <p className="flow-step-copy">{props.t("handover.telegramCopy")}</p>
              <p className="handover-telegram-note">{props.t("handover.telegramOwnerOnly")}</p>
            </>
          )}
        </li>

        {/* Step 2 (F133 numbering): the Owner Dashboard. Locked until the
            OWNERSHIP step with a NAMED reason (the beta testers pattern); done
            on the client's own confirmation that the dashboard opened. */}
        <li
          className={`flow-step${dashboardDone ? " flow-step-done" : ownershipDone ? "" : " flow-step-locked"}`}
          data-handover-step="2"
          data-handover-step-state={dashboardDone ? "done" : ownershipDone ? "open" : "locked"}
        >
          <header className="flow-step-head">
            <span className={`flow-step-num step-number-token${dashboardDone ? " flow-step-num-done is-done" : ownershipDone ? "" : " is-locked"}`} aria-hidden="true">{dashboardDone ? "✓" : "2"}</span>
            <h2>{props.t("handover.step2Title")}</h2>
            {/* F118: the step is open - the client really can register and
                copy their key - but the half that needs the owner role is
                ours until we set it, so the pill names whose turn that is
                (one hue per owner: our own work is the team's blue). */}
            {ownershipDone && !dashboardDone && !ownerRoleReady && (
              <span className="status-pill pill-team">{props.t("handover.ownerRolePill")}</span>
            )}
          </header>
          {!ownershipDone ? (
            <p className="flow-step-lock">{props.t("handover.step2Lock")}</p>
          ) : (
            <>
              <p className="flow-step-copy">{props.t("handover.step2Copy")}</p>

              <div className="license-key-block" data-license-key-state={licenseKey ? "ready" : selling ? "done" : "preparing"}>
                <p className="card-kicker">{props.t("handover.licenseKeyEyebrow")}</p>
                {licenseKey ? (
                  <>
                    <LicenseKeyRow value={licenseKey} t={props.t} />
                    <p className="license-key-own">{props.t("handover.licenseKeyOwn")}</p>
                  </>
                ) : (
                  // Honest state: no empty field. Either the key is still on
                  // its way from the instance, or (finished historic client)
                  // the licence already lives in their dashboard account.
                  <>
                    <p className="license-key-preparing">{props.t(selling ? "handover.licenseKeyUsed" : "handover.licenseKeyPreparing")}</p>
                    {!selling && licenseDelayed && (
                      <p className="handover-fact-delayed">{props.t("handover.licenseKeyDelayed")}</p>
                    )}
                  </>
                )}
              </div>

              {!dashboardDone && (
                <ol className="handover-dashboard-flow handover-substeps" data-dashboard-flow="signup">
                  <li>
                    <span className="handover-dashboard-number" aria-hidden="true">1</span>
                    <div>
                      <h3>{props.t("handover.flow.signupTitle")}</h3>
                      <p>{props.t("handover.flow.signupCopy")}</p>
                      {/* F132/F133c: the address carries `?licenseKey=` the
                          moment we have a key to put in it. The platform PR
                          (buddy-fm/buddy#894) is NOT merged, so today the
                          parameter is a harmless no-op the sign-up page never
                          reads - and the copy therefore promises NOTHING about
                          prefilling. The copy field above stays the client's
                          real path either way. Rendered only with a released
                          key, the same withholding the field itself obeys
                          (F105): a link naming an empty key is a lie. */}
                      {licenseKey && (
                        <a
                          className="secondary-button compact-button handover-signup-link"
                          href={`${dashboardBase}/sign-up?licenseKey=${encodeURIComponent(licenseKey)}`}
                          target="_blank"
                          rel="noreferrer"
                          data-signup-license-key="true"
                        >
                          {props.t("handover.flow.signupOpen")}
                        </a>
                      )}
                    </div>
                  </li>
                  {/* F133b: after the sign-up the dashboard takes over BY
                      ITSELF and shows this whole screen. There is no "Uz mam
                      experta" choice in the live flow, so the substep that used
                      to name one is gone. 🔴 This is also where the F105 note
                      about `/update` is reversed: the product asks for the
                      command on a screen of its own, so the walkthrough does
                      too. Every label here is the dashboard's own
                      (pages.linkBotPending.*). */}
                  <li>
                    <span className="handover-dashboard-number" aria-hidden="true">2</span>
                    <div>
                      <h3>{props.t("handover.flow.syncTitle")}</h3>
                      <p>{props.t("handover.flow.syncCopy")}</p>
                      <ol className="handover-screen-steps" data-dashboard-screen="sync">
                        <li>
                          {botUsername
                            ? <>{props.t("handover.flow.syncOpenTelegramKnown")} <code>@{botUsername}</code></>
                            : props.t("handover.flow.syncOpenTelegram")}
                        </li>
                        <li>{props.t("handover.flow.syncUpdate")} <code>/update</code></li>
                        <li>{props.t("handover.flow.syncCheck")}</li>
                      </ol>
                    </div>
                  </li>
                  {/* F118: /connectWeb and /myLicense are OWNER-role commands.
                      Until the role lands they answer nothing, so this substep
                      says so plainly instead of sending the client to a bot
                      that will stay silent. Everything above it (registration,
                      the licence key) works during the wait. */}
                  <li data-substep-waiting={ownerRoleReady ? "false" : "true"}>
                    <span className="handover-dashboard-number" aria-hidden="true">3</span>
                    <div>
                      <h3>{props.t("handover.flow.connectTitle")}</h3>
                      <p>{props.t("handover.flow.connectCopy")}</p>
                      <ol className="handover-screen-steps" data-dashboard-screen="connect">
                        <li>{props.t("handover.flow.connectLicense")}</li>
                        <li>{props.t("handover.flow.connectCode")}</li>
                      </ol>
                      {botUsername && ownerRoleReady && (
                        <a className="handover-bot-chat" href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer">
                          {props.t("handover.flow.openBotChat").replace("{bot}", `@${botUsername}`)}
                        </a>
                      )}
                      <small className={ownerRoleReady ? undefined : "handover-role-waiting"}>
                        {props.t(ownerRoleReady ? "handover.flow.codeRoleReady" : "handover.flow.codeRoleWaiting")}
                      </small>
                    </div>
                  </li>
                </ol>
              )}

              {dashboardDone ? (
                <div className="handover-dashboard-done">
                  <p className="message success-message" role="status">{props.t(selling ? "handover.sellingDashboard" : "handover.confirmedNote")}</p>
                  <a className="secondary-button compact-button" href={dashboardBase} target="_blank" rel="noreferrer">
                    {props.t("handover.openDashboard")}
                  </a>
                </div>
              ) : !ownerRoleReady ? (
                // A control that cannot do its job is not drawn as a control
                // (the partner console's P-18 law, on the client's side): the
                // confirmation asserts the dashboard shows the instance, and
                // the server refuses it by name before the role exists.
                <p className="flow-step-lock handover-confirm-wait">{props.t("handover.confirmOwnerRoleWait")}</p>
              ) : props.membershipRole === "owner" && props.csrfToken ? (
                <div className="handover-confirm">
                  <button className="primary-button" type="button" disabled={confirming} onClick={() => void confirmDashboard()}>
                    {confirming ? props.t("handover.confirmSending") : props.t("handover.confirmDashboard")}
                  </button>
                  <ActionError t={props.t} issue={confirmIssue} />
                </div>
              ) : (
                <p className="handover-telegram-note">{props.t("handover.confirmOwnerOnly")}</p>
              )}

              {!dashboardDone && (
                <a className="quiet-link handover-docs-link" href={docsUrl} target="_blank" rel="noreferrer">
                  {props.t("handover.docsLink")}
                </a>
              )}
            </>
          )}
        </li>
      </ol>

      {(license || accesses.length > 0) && <div className="handover-resource-grid">
        {license && (
        <article className="handover-resource-card">
          <p className="card-kicker">{props.t("handover.licenseEyebrow")}</p><h2>{props.t("handover.licenseTitle")}</h2>
          <strong>{String(license.type)}</strong><p>{String(license.description)}</p><small>{props.t("handover.licenseValid").replace("{date}", formatDate(String(license.valid_from), props.locale))}</small>
        </article>
        )}
        {accesses.length > 0 && (
        <article className="handover-resource-card">
          <p className="card-kicker">{props.t("handover.accessEyebrow")}</p><h2>{props.t("handover.accessTitle")}</h2>
          <ul>{accesses.map((access) => <li key={String(access.label)}>{access.url ? <a href={String(access.url)} target="_blank" rel="noreferrer">{String(access.label)}</a> : String(access.label)}</li>)}</ul>
        </article>
        )}
      </div>}
      <ActionError t={props.t} issue={props.actionError} />
    </section>
  );
}

function LicenseKeyRow({ value, t }: { value: string; t: (key: string) => string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard blocked: the client selects the visible key by hand.
    }
  }
  return (
    <div className="invite-link-row license-key-row">
      <input
        className="invite-link-input license-key-input"
        type="text"
        readOnly
        value={value}
        aria-label={t("handover.licenseKeyLabel")}
        onFocus={(event) => event.target.select()}
      />
      <button className="secondary-button compact-button" type="button" onClick={() => void copy()}>
        {copied ? t("handover.licenseKeyCopied") : t("handover.licenseKeyCopy")}
      </button>
    </div>
  );
}
